import { motion } from "framer-motion";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

export default function Ecommerce() {
  const features = [
    {
      title: "Payment Integration",
      description: "Secure and fast payment gateways supporting cards, UPI, wallets & net banking.",
      icon: "fas fa-credit-card"
    },
    {
      title: "Inventory Management",
      description: "Smart inventory control with real-time stock updates and alerts.",
      icon: "fas fa-boxes"
    },
    {
      title: "Order Processing",
      description: "Smooth order flow from cart to checkout, shipping and delivery.",
      icon: "fas fa-shopping-bag"
    },
    {
      title: "Multi-platform Support",
      description: "Pixel-perfect experience across mobile, tablet and desktop devices.",
      icon: "fas fa-mobile-alt"
    },
    {
      title: "SEO Optimization",
      description: "SEO-ready structure to boost your product visibility on search engines.",
      icon: "fas fa-search"
    },
    {
      title: "Analytics Dashboard",
      description: "Powerful insights to track revenue, orders, customers & growth.",
      icon: "fas fa-chart-line"
    }
  ];

  return (
    <div
      className="min-h-screen overflow-x-hidden bg-white"
      style={{ fontFamily: "'Roboto', sans-serif" }}
    >
      <Navigation />

   <section className="relative flex items-center justify-center overflow-hidden bg-[#040814] text-white pt-28 pb-8">
  {/* Background */}
  <div className="absolute inset-0"></div>

  <div className="container mx-auto px-6 relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className="text-center max-w-4xl mx-auto"
    >
      <div className="flex items-center justify-center gap-3 mb-6 text-sm text-slate-300">
        <Link href="/service" className="hover:text-white transition">
          Services
        </Link>
        <i className="fas fa-chevron-right text-xs" />
        <span className="text-slate-400">Ecommerce Development</span>
      </div>

      <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6">
        Build Powerful & <br />
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
          Scalable Ecommerce Stores
        </span>
      </h1>

      <p className="text-lg md:text-xl text-slate-300 mb-10">
        We design high-converting, fast and secure ecommerce platforms
        tailored to your business goals and customer needs.
      </p>
    </motion.div>
  </div>
</section>

      {/* ================= FEATURES (REPEAT ANIMATION) ================= */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.7 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-5">
              Premium Ecommerce <span className="text-blue-600">Features</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Everything you need to run, manage and scale your online business.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="group bg-white rounded-3xl p-8 border border-slate-100 shadow-xl hover:shadow-2xl transition-all"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-400 flex items-center justify-center mb-6 group-hover:scale-110 transition">
                  <i className={`${feature.icon} text-white text-xl`} />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA (REPEAT ANIMATION) ================= */}
      <section className="py-24 bg-gradient-to-br from-[#040814] via-[#0b1b3a] to-[#040814] text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Ready to Launch Your <br /> Ecommerce Business?
            </h2>

            <p className="text-lg text-slate-300 max-w-3xl mx-auto mb-10">
              Build a secure, scalable and conversion-focused ecommerce platform
              with our expert team.
            </p>

            <Link href="/getintouch">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="px-5 py-3 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-2xl font-bold text-md shadow-xl"
              >
                Start Your Project
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
